import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { UserCredits, CreditTransactionReason } from '@/types/credits';
import { ensureSession } from '@/lib/engagementPoints';

interface UseUserCreditsReturn {
  credits: UserCredits | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  deductCredit: (reason: CreditTransactionReason) => Promise<boolean>;
  hasEnoughCredits: (amount?: number) => boolean;
  canUseAiAssist: () => boolean;
  useAiAssist: () => Promise<boolean>;
}

const creditsCache = new Map<string, UserCredits>();
const creditsRequests = new Map<string, Promise<UserCredits>>();

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function loadCredits(userId: string, force = false): Promise<UserCredits> {
  if (!force) {
    const cached = creditsCache.get(userId);
    if (cached) return cached;
    const pending = creditsRequests.get(userId);
    if (pending) return pending;
  }

  const request = (async () => {
    await ensureSession();
    let lastError: unknown = null;

    for (let attempt = 1; attempt <= 3; attempt += 1) {
      const { data, error } = await supabase
        .from('user_credits')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (!error && data) {
        const value = data as UserCredits;
        creditsCache.set(userId, value);
        return value;
      }

      lastError = error ?? new Error('Account record unavailable');
      if (attempt < 3) await wait(1000 * attempt);
    }

    throw lastError;
  })();

  creditsRequests.set(userId, request);
  try {
    return await request;
  } finally {
    if (creditsRequests.get(userId) === request) creditsRequests.delete(userId);
  }
}

export function useUserCredits(): UseUserCreditsReturn {
  const { user, isHydrated } = useAuth();
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCredits = useCallback(async (force = false) => {
    if (!isHydrated) return;

    if (!user) {
      setCredits(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await loadCredits(user.id, force);
      setCredits(data);
      setError(null);
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching credits:', err);
      setError('Failed to load credits');
      setIsLoading(false);
    }
  }, [isHydrated, user?.id]);

  useEffect(() => {
    fetchCredits(false);
  }, [fetchCredits]);

  const hasEnoughCredits = useCallback(
    (amount: number = 1): boolean => {
      // If credits haven't loaded yet (or a transient fetch error left the
      // state null), don't block the user — the deduct_announcement_credit
      // RPC is atomic and will reject if the true balance is insufficient.
      // Only return false when we *know* the balance is too low.
      if (!credits) return true;
      return credits.announcement_credits >= amount;
    },
    [credits]
  );

  const canUseAiAssist = useCallback((): boolean => {
    if (!credits) return false;
    if (credits.has_purchased_bundle) return true;
    return credits.ai_assist_uses_remaining > 0;
  }, [credits]);

  const useAiAssist = useCallback(async (): Promise<boolean> => {
    if (!user || !credits) return false;

    if (credits.has_purchased_bundle) return true;
    if (credits.ai_assist_uses_remaining <= 0) return false;

    try {
      const { data, error: rpcError } = await supabase.rpc('consume_ai_assist');
      if (rpcError) throw rpcError;
      const result = (data as { success?: boolean; unlimited?: boolean; remaining?: number } | null) || {};
      if (!result.success) return false;

      if (!result.unlimited) {
        setCredits((prev) =>
          prev
            ? {
                ...prev,
                ai_assist_uses_remaining:
                  typeof result.remaining === 'number'
                    ? result.remaining
                    : Math.max(0, prev.ai_assist_uses_remaining - 1),
              }
            : null
        );
        const cached = creditsCache.get(user.id);
        if (cached) {
          creditsCache.set(user.id, {
            ...cached,
            ai_assist_uses_remaining:
              typeof result.remaining === 'number'
                ? result.remaining
                : Math.max(0, cached.ai_assist_uses_remaining - 1),
          });
        }
      }

      return true;
    } catch (err) {
      console.error('Error using AI assist:', err);
      return false;
    }
  }, [user?.id, credits]);

  const deductCredit = useCallback(
    async (reason: CreditTransactionReason): Promise<boolean> => {
      if (!user || !credits) return false;
      if (credits.announcement_credits <= 0) return false;

      try {
        const { data, error: rpcError } = await supabase.rpc(
          'deduct_announcement_credit',
          { p_user_id: user.id, p_reason: reason }
        );

        if (rpcError) throw rpcError;
        if (data === false) return false;

        setCredits((prev) =>
          prev
            ? {
                ...prev,
                announcement_credits: prev.announcement_credits - 1,
              }
            : null
        );
        const cachedCredits = creditsCache.get(user.id);
        if (cachedCredits) {
          creditsCache.set(user.id, {
            ...cachedCredits,
            announcement_credits: Math.max(0, cachedCredits.announcement_credits - 1),
          });
        }

        return true;
      } catch (err) {
        console.error('Error deducting credit:', err);
        return false;
      }
    },
    [user?.id, credits]
  );

  return {
    credits,
    isLoading,
    error,
    refetch: () => fetchCredits(true),
    deductCredit,
    hasEnoughCredits,
    canUseAiAssist,
    useAiAssist,
  };
}
